import classes from "./AppHeader.module.css";
import { LoadingContent } from "@/components/LoadingContent";
import {
  Anchor,
  Box,
  Burger,
  Button,
  Container,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  Tooltip,
} from "@mantine/core";
import { Link, useLocation } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { signOutOfWebApp, useAppSession } from "@/features/auth/session";
import { useCurrentProfile } from "@/features/profile/queries";
import { useCurrentPlan } from "@/features/subscriptions/queries";
import { profileIdentity } from "@/features/profile/identity";
import { HeaderProfile } from "@/components/HeaderProfile";
import { AppLogo } from "@/components/AppLogo";
import { accountAccessOptions } from "@/features/auth/access";
import { canAdminister } from "@/features/admin-users/access";
import { listSearchSchema } from "@/features/admin-users/schema";
import { useState } from "react";

const mainLinks = [
  { to: "/search", label: "Search", paths: ["/search"] },
  { to: "/trips", label: "Trips", paths: ["/trips"] },
  { to: "/packing-lists", label: "Packing Lists", paths: ["/packing-lists"] },
  {
    to: "/countries",
    label: "Country Explorer",
    paths: ["/countries", "/country"],
  },
  {
    to: "/travel-bucket-list",
    label: "Bucket List",
    paths: ["/travel-bucket-list"],
  },
  { to: "/statistics", label: "Statistics", paths: ["/statistics"] },
  { to: "/translate", label: "Translate", paths: ["/translate"] },
] as const;

export function AppHeader() {
  const location = useLocation();
  const session = useAppSession({ refetchOnMount: false });
  const user = session.error ? undefined : session.data?.user;
  const account = useQuery({
    ...accountAccessOptions(user?.id ?? ""),
    enabled: Boolean(user),
    refetchOnMount: false,
  });
  const showAdmin =
    Boolean(user) && !account.error && canAdminister(account.data, user?.id);
  const profile = useCurrentProfile(user?.id);
  const plan = useCurrentPlan(user?.id);
  const identity = user ? profileIdentity(user, profile.data) : null;
  const logout = useMutation({ mutationFn: signOutOfWebApp });
  const [navigationOpened, setNavigationOpened] = useState(false);
  const isActive = (paths: readonly string[]) =>
    paths.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`),
    );

  return (
    <Box
      component="header"
      py="sm"
      style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}
    >
      <Container size="xl" className={classes.container}>
        <Group justify="space-between" gap="sm" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
            <Tooltip
              label="TripBears home"
              events={{ hover: true, focus: true, touch: false }}
              interactive
            >
              <Anchor
                renderRoot={(props) => <Link {...props} to="/" />}
                underline="never"
                aria-label="TripBears home"
                miw={44}
                mih={44}
                display="inline-flex"
                style={{ alignItems: "center", justifyContent: "center" }}
              >
                <Group component="span" gap="xs" wrap="nowrap">
                  <AppLogo size={36} alt="" />
                  <Text
                    component="span"
                    fw={700}
                    size="lg"
                    className={classes.wordmark}
                  >
                    TripBears
                  </Text>
                </Group>
              </Anchor>
            </Tooltip>
            {user ? (
              <Box
                component="nav"
                aria-label="Main navigation"
                className={classes.collapsedNav}
              >
                <Menu
                  opened={navigationOpened}
                  onChange={setNavigationOpened}
                  position="bottom-start"
                  width={240}
                  shadow="md"
                  loop={false}
                  withinPortal={false}
                  trapFocus={false}
                  menuItemTabIndex={0}
                >
                  <Tooltip
                    label="Navigation"
                    disabled={navigationOpened}
                    events={{ hover: true, focus: true, touch: false }}
                    interactive
                  >
                    <Menu.Target>
                      <Burger
                        opened={navigationOpened}
                        size="sm"
                        w={44}
                        h={44}
                        aria-label="Main navigation menu"
                      />
                    </Menu.Target>
                  </Tooltip>
                  <Menu.Dropdown>
                    <ScrollArea.Autosize
                      mah="min(480px, calc(100dvh - 80px))"
                      type="auto"
                    >
                      {mainLinks.map((link) => (
                        <Menu.Item
                          key={link.to}
                          renderRoot={(props) => (
                            <Link {...props} to={link.to} />
                          )}
                          aria-current={
                            isActive(link.paths) ? "page" : undefined
                          }
                          mih={44}
                        >
                          {link.label}
                        </Menu.Item>
                      ))}
                      {showAdmin ? (
                        <>
                          <Menu.Divider />
                          <Menu.Label>Admin</Menu.Label>
                          <Menu.Item
                            mih={44}
                            renderRoot={(props) => (
                              <Link {...props} to="/admin/general" />
                            )}
                            aria-current={
                              location.pathname === "/admin/general"
                                ? "page"
                                : undefined
                            }
                          >
                            General
                          </Menu.Item>
                          <Menu.Item
                            mih={44}
                            renderRoot={(props) => (
                              <Link
                                {...props}
                                to="/admin/users"
                                search={listSearchSchema.parse({})}
                              />
                            )}
                            aria-current={
                              location.pathname.startsWith("/admin/users")
                                ? "page"
                                : undefined
                            }
                          >
                            Users
                          </Menu.Item>
                          <Menu.Item
                            mih={44}
                            renderRoot={(props) => (
                              <Link {...props} to="/admin/plans" />
                            )}
                            aria-current={
                              location.pathname === "/admin/plans"
                                ? "page"
                                : undefined
                            }
                          >
                            Plans
                          </Menu.Item>
                          <Menu.Item
                            mih={44}
                            renderRoot={(props) => (
                              <Link {...props} to="/admin/legal" />
                            )}
                            aria-current={
                              location.pathname === "/admin/legal"
                                ? "page"
                                : undefined
                            }
                          >
                            Legal
                          </Menu.Item>
                          <Menu.Item
                            mih={44}
                            renderRoot={(props) => (
                              <Link {...props} to="/admin/attributions" />
                            )}
                            aria-current={
                              location.pathname === "/admin/attributions"
                                ? "page"
                                : undefined
                            }
                          >
                            Attributions
                          </Menu.Item>
                          <Menu.Item
                            mih={44}
                            renderRoot={(props) => (
                              <Link {...props} to="/admin/app-store-links" />
                            )}
                            aria-current={
                              location.pathname === "/admin/app-store-links"
                                ? "page"
                                : undefined
                            }
                          >
                            App links
                          </Menu.Item>
                          <Menu.Item
                            mih={44}
                            renderRoot={(props) => (
                              <Link {...props} to="/admin/email-templates" />
                            )}
                            aria-current={
                              location.pathname.startsWith(
                                "/admin/email-templates",
                              )
                                ? "page"
                                : undefined
                            }
                          >
                            Email Templates
                          </Menu.Item>
                          <Menu.Item
                            mih={44}
                            renderRoot={(props) => (
                              <Link {...props} to="/admin/authentication" />
                            )}
                            aria-current={
                              location.pathname === "/admin/authentication"
                                ? "page"
                                : undefined
                            }
                          >
                            Authentication
                          </Menu.Item>
                          <Menu.Item
                            mih={44}
                            renderRoot={(props) => (
                              <Link {...props} to="/admin/queues" />
                            )}
                            aria-current={
                              location.pathname === "/admin/queues"
                                ? "page"
                                : undefined
                            }
                          >
                            Queues
                          </Menu.Item>
                          <Menu.Item
                            mih={44}
                            renderRoot={(props) => (
                              <Link {...props} to="/admin/jobs" />
                            )}
                            aria-current={
                              location.pathname === "/admin/jobs"
                                ? "page"
                                : undefined
                            }
                          >
                            Jobs
                          </Menu.Item>
                        </>
                      ) : null}
                    </ScrollArea.Autosize>
                  </Menu.Dropdown>
                </Menu>
              </Box>
            ) : null}
            {user ? (
              <Group
                component="nav"
                aria-label="Main navigation"
                gap="xs"
                wrap="nowrap"
                miw={0}
                className={classes.desktopNav}
              >
                {mainLinks.map((link) => {
                  const active = isActive(link.paths);
                  return (
                    <NavLink
                      key={link.to}
                      renderRoot={(props) => <Link {...props} to={link.to} />}
                      label={link.label}
                      active={active}
                      aria-current={active ? "page" : undefined}
                      w="auto"
                      mih={44}
                      bdrs="sm"
                    />
                  );
                })}
                {showAdmin ? (
                  <Menu
                    position="bottom-start"
                    width={180}
                    loop={false}
                    withinPortal={false}
                    trapFocus={false}
                    menuItemTabIndex={0}
                  >
                    <Menu.Target>
                      <Button
                        variant={
                          location.pathname.startsWith("/admin/")
                            ? "light"
                            : "subtle"
                        }
                        mih={44}
                      >
                        Admin
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        mih={44}
                        renderRoot={(props) => (
                          <Link {...props} to="/admin/messages" />
                        )}
                      >
                        Messages
                      </Menu.Item>
                      <Menu.Item
                        mih={44}
                        renderRoot={(props) => (
                          <Link {...props} to="/admin/general" />
                        )}
                        aria-current={
                          location.pathname === "/admin/general"
                            ? "page"
                            : undefined
                        }
                      >
                        General
                      </Menu.Item>
                      <Menu.Item
                        mih={44}
                        renderRoot={(props) => (
                          <Link
                            {...props}
                            to="/admin/users"
                            search={listSearchSchema.parse({})}
                          />
                        )}
                        aria-current={
                          location.pathname.startsWith("/admin/users")
                            ? "page"
                            : undefined
                        }
                      >
                        Users
                      </Menu.Item>
                      <Menu.Item
                        mih={44}
                        renderRoot={(props) => (
                          <Link {...props} to="/admin/plans" />
                        )}
                        aria-current={
                          location.pathname === "/admin/plans"
                            ? "page"
                            : undefined
                        }
                      >
                        Plans
                      </Menu.Item>
                      <Menu.Item
                        mih={44}
                        renderRoot={(props) => (
                          <Link {...props} to="/admin/legal" />
                        )}
                        aria-current={
                          location.pathname === "/admin/legal"
                            ? "page"
                            : undefined
                        }
                      >
                        Legal
                      </Menu.Item>
                      <Menu.Item
                        mih={44}
                        renderRoot={(props) => (
                          <Link {...props} to="/admin/attributions" />
                        )}
                        aria-current={
                          location.pathname === "/admin/attributions"
                            ? "page"
                            : undefined
                        }
                      >
                        Attributions
                      </Menu.Item>
                      <Menu.Item
                        mih={44}
                        renderRoot={(props) => (
                          <Link {...props} to="/admin/app-store-links" />
                        )}
                        aria-current={
                          location.pathname === "/admin/app-store-links"
                            ? "page"
                            : undefined
                        }
                      >
                        App links
                      </Menu.Item>
                      <Menu.Item
                        mih={44}
                        renderRoot={(props) => (
                          <Link {...props} to="/admin/email-templates" />
                        )}
                        aria-current={
                          location.pathname.startsWith("/admin/email-templates")
                            ? "page"
                            : undefined
                        }
                      >
                        Email Templates
                      </Menu.Item>
                      <Menu.Item
                        mih={44}
                        renderRoot={(props) => (
                          <Link {...props} to="/admin/authentication" />
                        )}
                        aria-current={
                          location.pathname === "/admin/authentication"
                            ? "page"
                            : undefined
                        }
                      >
                        Authentication
                      </Menu.Item>
                      <Menu.Item
                        mih={44}
                        renderRoot={(props) => (
                          <Link {...props} to="/admin/queues" />
                        )}
                        aria-current={
                          location.pathname === "/admin/queues"
                            ? "page"
                            : undefined
                        }
                      >
                        Queues
                      </Menu.Item>
                      <Menu.Item
                        mih={44}
                        renderRoot={(props) => (
                          <Link {...props} to="/admin/jobs" />
                        )}
                        aria-current={
                          location.pathname === "/admin/jobs"
                            ? "page"
                            : undefined
                        }
                      >
                        Jobs
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                ) : null}
              </Group>
            ) : null}
          </Group>
          <Group
            component="nav"
            aria-label="Account navigation"
            gap="xs"
            wrap="nowrap"
            miw={0}
            style={{ maxWidth: "min(320px, 45%)", flexShrink: 1 }}
          >
            {session.isPending ? (
              <Box w={{ base: 44, sm: 144 }} miw={0}>
                <LoadingContent
                  label="Checking session…"
                  variant="rows"
                  rows={1}
                  height={44}
                />
              </Box>
            ) : user && identity ? (
              <HeaderProfile
                name={identity.name}
                plan={plan.data}
                email={user.email}
                imageUrl={identity.imageUrl}
                active={location.pathname === "/profile"}
                settingsActive={location.pathname.startsWith("/settings")}
                mobileAppsActive={location.pathname === "/mobile-apps"}
                mcpActive={location.pathname === "/mcp"}
                signOutPending={logout.isPending}
                signOutError={logout.error?.message ?? null}
                onSignOut={() => logout.mutate()}
              />
            ) : (
              <Button
                renderRoot={(props) => (
                  <Link
                    {...props}
                    to="/auth/sign-in"
                    search={{
                      next: location.pathname.startsWith("/auth/")
                        ? undefined
                        : location.href,
                    }}
                  />
                )}
              >
                Sign in
              </Button>
            )}
          </Group>
        </Group>
      </Container>
    </Box>
  );
}
